import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import HomePage from './components/Homepage';
import TestSelectionPage from './components/TestSelectionPage';
import IPMATReportCard from './components/IPMATReportCard';
import { normalizeIPMATCategory } from './utils/ipmatCategory';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Wrapper component to use useNavigate
const AppContent = ({
  currentStudent, setCurrentStudent, selectedTest, setSelectedTest,
  ipmatTests, isLoading,
  jwtToken, setJwtToken, studentCategory, setStudentCategory
}) => {
  const navigate = useNavigate();

  // Handle student login success
  const handleStudentLogin = (studentData) => {
    const { token: loginToken, ...safeStudentData } = studentData;
    if (loginToken) setJwtToken(loginToken);
    setCurrentStudent(safeStudentData);
    localStorage.setItem('currentStudent', JSON.stringify(safeStudentData));
    navigate('/tests');
  };

  // Handle IPMAT test selection
  const handleIPMATTestSelect = (test) => {
    const selected = {
      ...test,
      testId: String(test.testId),
      category: normalizeIPMATCategory(test.category),
    };
    setSelectedTest(selected);
    localStorage.setItem('selectedTest', JSON.stringify(selected));
    if (selected.category) setStudentCategory(selected.category);
    navigate('/ipmat/report');
  };

  // Handle logout
  const handleLogout = () => {
    setCurrentStudent(null);
    setSelectedTest(null);
    setJwtToken(null);
    setStudentCategory(null);
    localStorage.removeItem('currentStudent');
    localStorage.removeItem('selectedTest');
    localStorage.removeItem('jwtToken');
    // Category is NOT removed from localStorage because it's not stored there anymore
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-xl font-semibold text-slate-700">Loading application...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          currentStudent ? (
            selectedTest ? (
              <Navigate to="/ipmat/report" replace />
            ) : (
              <Navigate to="/tests" replace />
            )
          ) : (
            <HomePage onStudentLogin={handleStudentLogin} />
          )
        }
      />

      <Route
        path="/tests"
        element={
          currentStudent ? (
            <TestSelectionPage
              studentPin={currentStudent.imsPin}
              studentName={currentStudent.name || currentStudent.imsPin}
              category={studentCategory}
              ipmatTests={ipmatTests}
              onSelectIPMATTest={handleIPMATTestSelect}
              onLogout={handleLogout}
              jwtToken={jwtToken}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/ipmat/report"
        element={
          (() => {
            // URL-param bypass for dev/testing: render report directly from
            // query params without requiring a logged-in student session.
            const params = new URLSearchParams(window.location.search);
            const urlPin = params.get('pin');
            // Upstream MyIMS test lists expose the ids as asset_id/assessment_id;
            // shared report links use those camelCase forms, so accept them as
            // aliases of testId/assetId (plus the snake_case variants).
            const urlTestId = params.get('testId') || params.get('test_id')
              || params.get('assessmentId') || params.get('assessment_id');
            const urlToken = params.get('token');

            // Minimal URL: assetId + testId + category + token (pin derived from token)
            const urlAssetId = params.get('assetId') || params.get('asset_id');
            const urlCategory = params.get('category');

            if (urlTestId && urlToken) {
              return (
                <IPMATReportCard
                  studentPin={urlPin || ''}
                  studentName={urlPin || ''}
                  testId={urlTestId}
                  assetId={urlAssetId || ''}
                  testName={params.get('testName') || ''}
                  testDate={params.get('testDate') || ''}
                  category={normalizeIPMATCategory(urlCategory)}
                  token={urlToken}
                />
              );
            }

            // Normal flow: require logged-in student
            if (currentStudent && selectedTest) {
              return (
                <IPMATReportCard
                  studentPin={currentStudent.imsPin}
                  studentName={currentStudent?.name || currentStudent?.imsPin}
                  testId={selectedTest.testId}
                  assetId={selectedTest.assetId || ''}
                  testName={selectedTest.testName || ''}
                  testDate={selectedTest.testDate || ''}
                  category={normalizeIPMATCategory(selectedTest.category)}
                  token={jwtToken}
                />
              );
            }

            return <Navigate to="/" replace />;
          })()
        }
      />
    </Routes>
  );
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Student session state
  const [currentStudent, setCurrentStudent] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [ipmatTests, setIpmatTests] = useState([]);

  // JWT token — populated from URL ?token= or localStorage
  const [jwtToken, setJwtToken] = useState(null);

  // Student category — fetched once in App.js, passed as prop to children
  const [studentCategory, setStudentCategory] = useState(null);

  // Load data from localStorage on initial render + detect JWT in URL
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // ── Check URL params for JWT token ──
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        // Support camelCase (testId/assetId), snake_case (test_id/asset_id)
        // and upstream MyIMS naming (assessmentId/assessment_id)
        const urlTestId = params.get('testId') || params.get('test_id')
          || params.get('assessmentId') || params.get('assessment_id');

        // Priority: URL token > localStorage token
        const savedToken = localStorage.getItem('jwtToken');
        const activeToken = urlToken || savedToken;

        if (activeToken) {
          console.log('🔑 JWT token detected — initiating API-first login flow');
          setJwtToken(activeToken);
          localStorage.setItem('jwtToken', activeToken);
          if (urlToken) {
            // Remove the credential from the address bar, browser history and
            // referrer surface immediately after capturing it.
            const cleanParams = new URLSearchParams(window.location.search);
            cleanParams.delete('token');
            const cleanQuery = cleanParams.toString();
            window.history.replaceState({}, document.title, `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}`);
          }

          // Auto-fetch profile from API
          try {
            const profileRes = await fetch(`${API_BASE_URL}/api/ipmat/student/profile`, {
              headers: { Authorization: `Bearer ${activeToken}` }
            });
            const profileData = await profileRes.json();

            if (profileData.success) {
              const profile = profileData.data;

              // ── Check mvp-ai-insights DB for stored category ──
              let resolvedCategory = null;
              try {
                const categoryRes = await fetch(`${API_BASE_URL}/api/ipmat/student/${profile.studentPin}/category`, {
                  headers: { Authorization: `Bearer ${activeToken}` }
                });
                const categoryData = await categoryRes.json();
                const storedCategory = categoryData?.data?.category ?? categoryData?.category;
                const categorySource = categoryData?.data?.categorySource ?? categoryData?.categorySource;
                // No fallback: a DEFAULT source means the student never picked a
                // category. We must NOT resolve one here — leaving it null makes the
                // report card prompt for it. Only an explicitly stored category counts.
                if (categoryData.success && storedCategory && categorySource !== 'DEFAULT') {
                  resolvedCategory = normalizeIPMATCategory(storedCategory);
                  setStudentCategory(resolvedCategory);
                  console.log(`📂 Category from DB: ${resolvedCategory} (source=${categorySource})`);
                } else {
                  console.log(`⚠️ No confirmed category (source=${categorySource || 'none'}) — will prompt on report open`);
                }
              } catch (catErr) {
                console.warn('⚠️ Could not fetch stored category:', catErr.message);
              }

              // If no stored category, continue to tests page - category modal will show on AI Insights click
              if (!resolvedCategory) {
                console.log('⚠️ No stored category found - will prompt on AI Insights click');
              }

              const studentData = {
                imsPin: profile.studentPin,
                name: profile.name,
                loginMethod: 'jwt'
              };

              setCurrentStudent(studentData);
              localStorage.setItem('currentStudent', JSON.stringify(studentData));

              // ── Fetch IPMAT tests ──
              try {
                const ipmatRes = await fetch(`${API_BASE_URL}/api/ipmat/student/tests`, {
                  headers: { Authorization: `Bearer ${activeToken}` }
                });
                const ipmatData = await ipmatRes.json();
                if (ipmatData.success && ipmatData.tests) {
                  setIpmatTests(ipmatData.tests);
                  console.log(`📊 IPMAT tests: ${ipmatData.tests.length}`);
                }
              } catch (ipmatErr) {
                console.warn('⚠️ Could not fetch IPMAT tests:', ipmatErr.message);
              }
              // Category lives in MongoDB only — no localStorage write

              // If a specific testId was passed in URL, pre-select it.
              // Why the fallback below? When the user refreshes on /ipmat/report
              // after navigating there via handleIPMATTestSelect (which calls
              // navigate('/ipmat/report') with no query string), the URL has no
              // test_id. Without this fallback, the JWT branch below would
              // return early (line: setIsLoading(false); return;) and skip
              // the localStorage selectedTest restore further down — leaving
              // selectedTest === null and bouncing the user back to /tests.
              if (urlTestId) {
                const preSelected = {
                  testId: String(urlTestId),
                  assetId: params.get('assetId') || params.get('asset_id') || '',
                  testName: params.get('testName') || '',
                  testDate: params.get('testDate') || '',
                  category: normalizeIPMATCategory(resolvedCategory || params.get('category')),
                };
                setSelectedTest(preSelected);
                localStorage.setItem('selectedTest', JSON.stringify(preSelected));
              } else {
                const savedSelectedTest = localStorage.getItem('selectedTest');
                if (savedSelectedTest) {
                  try {
                    const parsed = JSON.parse(savedSelectedTest);
                    // Keep category in sync with whichever source resolved it
                    if (parsed) parsed.category = normalizeIPMATCategory(parsed.category || resolvedCategory);
                    setSelectedTest(parsed);
                    console.log(`🔄 Restored selectedTest from localStorage: ${parsed.testId}`);
                  } catch (parseErr) {
                    console.warn('⚠️ Could not parse saved selectedTest:', parseErr.message);
                  }
                }
              }

              // Store resolved category in App state for prop passing
              if (resolvedCategory) {
                setStudentCategory(resolvedCategory);
              }

              console.log(`✅ JWT auto-login complete: ${profile.studentPin}`);
            } else {
              console.warn('⚠️ JWT login: API returned failure, falling through to localStorage');
            }
          } catch (jwtErr) {
            console.warn('⚠️ JWT auto-login failed, falling through to manual login:', jwtErr.message);
          }

          setIsLoading(false);
          return;
        }

        // ── Normal localStorage restore flow ──
        const savedCurrentStudent = localStorage.getItem('currentStudent');
        const savedSelectedTest = localStorage.getItem('selectedTest');
        const savedJwt = localStorage.getItem('jwtToken');
        // Category is NOT read from localStorage — MongoDB is the single source of truth

        if (savedCurrentStudent) setCurrentStudent(JSON.parse(savedCurrentStudent));
        if (savedSelectedTest) setSelectedTest(JSON.parse(savedSelectedTest));
        if (savedJwt) setJwtToken(savedJwt);

        // ── Fetch category from MongoDB (not localStorage) ──
        // If we have a student PIN from restored state, fetch their category from DB
        if (savedCurrentStudent) {
          try {
            const parsed = JSON.parse(savedCurrentStudent);
            const pin = parsed.imsPin;
            if (pin) {
              const catRes = await fetch(`${API_BASE_URL}/api/ipmat/student/${pin}/category`, {
                headers: savedJwt ? { Authorization: `Bearer ${savedJwt}` } : {}
              });
              const catData = await catRes.json();
              const storedCategory = catData?.data?.category ?? catData?.category;
              const categorySource = catData?.data?.categorySource ?? catData?.categorySource;
              // No fallback: ignore DEFAULT source so the modal still prompts.
              if (catData.success && storedCategory && categorySource !== 'DEFAULT') {
                setStudentCategory(normalizeIPMATCategory(storedCategory));
                console.log(`📂 Category from DB (restore): ${storedCategory} (source=${categorySource})`);
              }
            }
          } catch (catErr) {
            console.warn('⚠️ Could not fetch category on restore:', catErr.message);
          }
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        localStorage.removeItem('currentStudent');
        localStorage.removeItem('selectedTest');
        // studentCategory is not in localStorage, no need to remove
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, []);

  return (
    <Router>
      <AppContent
        currentStudent={currentStudent}
        setCurrentStudent={setCurrentStudent}
        selectedTest={selectedTest}
        setSelectedTest={setSelectedTest}
        isLoading={isLoading}
        jwtToken={jwtToken}
        setJwtToken={setJwtToken}
        studentCategory={studentCategory}
        setStudentCategory={setStudentCategory}
        ipmatTests={ipmatTests}
      />
    </Router>
  );
};

export default App;
